/**
 * Browser history / beforeunload leave-warn patch (Spec1-P12 §12.8).
 * Kept outside React modules so sync confirm + BeforeUnloadEvent APIs stay lint-clean.
 */

let dirtyLeaveCount = 0;
let dirtyLeaveSuspended = false;
let beforeUnloadListenerAttached = false;
let activeLeaveTitle = 'Unsaved changes';
let activeLeaveMessage = 'You have unsaved changes. Leave without saving?';
let originalPushState: History['pushState'] | null = null;
let originalReplaceState: History['replaceState'] | null = null;

function attachBeforeUnloadListener(): void {
  if (beforeUnloadListenerAttached || dirtyLeaveSuspended || dirtyLeaveCount === 0) {
    return;
  }
  window.addEventListener('beforeunload', onBeforeUnload);
  beforeUnloadListenerAttached = true;
}

function detachBeforeUnloadListener(): void {
  if (!beforeUnloadListenerAttached) {
    return;
  }
  window.removeEventListener('beforeunload', onBeforeUnload);
  beforeUnloadListenerAttached = false;
}

function confirmLeaveSync(): boolean {
  const ask = Reflect.get(globalThis, 'confirm') as
    | ((message?: string) => boolean)
    | undefined;
  if (typeof ask !== 'function') {
    return true;
  }
  return ask(`${activeLeaveTitle}\n\n${activeLeaveMessage}`);
}

/** True when navigation leaves the current dashboard page (pathname or `section`), not in-page `tab`. */
function isLeavingPageNavigation(url: string | URL | null | undefined): boolean {
  if (url == null || url === '') {
    return false;
  }
  try {
    const next = new URL(String(url), window.location.origin);
    if (next.pathname !== window.location.pathname) {
      return true;
    }
    const currentSection = new URLSearchParams(window.location.search).get('section');
    const nextSection = next.searchParams.get('section');
    return currentSection !== nextSection;
  } catch {
    return true;
  }
}

function onBeforeUnload(event: BeforeUnloadEvent): void {
  event.preventDefault();
  Reflect.set(event, 'returnValue', '');
}

function onPopState(event: PopStateEvent): void {
  if (confirmLeaveSync()) {
    return;
  }
  event.stopImmediatePropagation();
  if (originalPushState != null) {
    originalPushState.call(window.history, window.history.state, '', window.location.href);
  }
}

export function acquireDirtyLeavePatch(title: string, message: string): void {
  activeLeaveTitle = title;
  activeLeaveMessage = message;
  dirtyLeaveCount += 1;
  if (dirtyLeaveCount !== 1) {
    return;
  }
  originalPushState = window.history.pushState.bind(window.history);
  originalReplaceState = window.history.replaceState.bind(window.history);

  window.history.pushState = ((...args: Parameters<History['pushState']>): void => {
    const url = args[2];
    if (isLeavingPageNavigation(url) && !confirmLeaveSync()) {
      return;
    }
    originalPushState?.(...args);
  }) as History['pushState'];

  window.history.replaceState = ((...args: Parameters<History['replaceState']>): void => {
    const url = args[2];
    if (isLeavingPageNavigation(url) && !confirmLeaveSync()) {
      return;
    }
    originalReplaceState?.(...args);
  }) as History['replaceState'];

  attachBeforeUnloadListener();
  window.addEventListener('popstate', onPopState, true);
}

export function releaseDirtyLeavePatch(): void {
  dirtyLeaveCount = Math.max(0, dirtyLeaveCount - 1);
  if (dirtyLeaveCount !== 0) {
    return;
  }
  if (originalPushState != null) {
    window.history.pushState = originalPushState;
    originalPushState = null;
  }
  if (originalReplaceState != null) {
    window.history.replaceState = originalReplaceState;
    originalReplaceState = null;
  }
  detachBeforeUnloadListener();
  window.removeEventListener('popstate', onPopState, true);
}

/**
 * Temporarily detach beforeunload while a controlled in-app reload runs (PWA Apply Update).
 * History leave-warn stays patched; only the unload prompt is suppressed so reload is not cancelled.
 * Call resumeDirtyLeaveAfterNavigation() when the reload fails or the watchdog fires.
 */
export function suspendDirtyLeaveForNavigation(): void {
  if (dirtyLeaveSuspended) {
    return;
  }
  dirtyLeaveSuspended = true;
  detachBeforeUnloadListener();
}

/** Restore beforeunload after a failed controlled reload when dirty leave-warn is still active. */
export function resumeDirtyLeaveAfterNavigation(): void {
  if (!dirtyLeaveSuspended) {
    return;
  }
  dirtyLeaveSuspended = false;
  attachBeforeUnloadListener();
}

/** Test-only: whether beforeunload is currently registered by the dirty-leave patch. */
export function isDirtyLeaveBeforeUnloadAttachedForTests(): boolean {
  return beforeUnloadListenerAttached;
}

/** Test-only reset for history leave-warn patch refcount. */
export function resetDirtyLeavePatchForTests(): void {
  dirtyLeaveSuspended = false;
  while (dirtyLeaveCount > 0) {
    releaseDirtyLeavePatch();
  }
}
